/**
 * Display-only hole blur for the scan viewer (screen space, every frame).
 *
 * A hole in a scan model is missing geometry; on the dark background it reads
 * as a black object. This pass fills the background that the model encloses
 * with a smooth blur of the surrounding photo colour. The model, its files and
 * picking are not changed, and the background outside the model's silhouette
 * stays exactly the viewer background (no halo).
 *
 * It is the same algorithm, with the same parameters, as the iOS app's recon
 * viewer (CaptureX ProjectsUI/ReconHoleBlur.metal, b3 item V1); the written
 * spec is .recon-data/logs/b3/v1/params.txt (v3). Keep both in sync.
 *
 * Per frame, in drawing-buffer pixels (GL rows, y up; the rule is symmetric):
 * 0. The scene renders into a 4x MSAA sRGB target cleared to transparent
 *    black: rgb = premultiplied linear colour, a = coverage.
 * 1. down4: 4 x 4 px cells, mean colour and coverage over in-image pixels.
 * 2. enclose: a cell holding background (coverage < 1) passes the rule when
 *    surface (a cell with coverage >= 0.5) lies within R in at least 6 of the
 *    8 directions (R = 12 % of the short side in cells; or the hole's chord
 *    along that axis is at most 2 R; stepping off the screen is a miss), and
 *    no missed direction runs on through background to the screen edge (bays
 *    and gaps that open onto the edge stay plain).
 * 3. feather: enclosed = the rule, and for a mixed cell no open neighbour the
 *    rule rejects; weight w = 1 inside, 0.25 / 0.5 / 0.75 within 1 / 2 / 3
 *    cells of open background that is not enclosed.
 * 4. push: coverage-weighted pyramid of 2 x 2 means, 6 levels.
 * 5. pull: level + (1 - weight) * bilinear(coarser pulled level).
 * 6. composite: an enclosed pixel's background share takes the pulled colour
 *    (weighted by w); a surface pixel 1 or 2 px from such a pixel blends
 *    toward it by 2/3 or 1/3; every other pixel is today's render,
 *    colour + (1 - coverage) * background.
 */
import * as THREE from "three";

export const HOLE_RADIUS_FRACTION = 0.12;
export const HOLE_MIN_DIRECTIONS = 6;
export const HOLE_SURFACE_COVERAGE = 0.5;
export const HOLE_PIXEL_COVERAGE = 0.5;
export const HOLE_FEATHER_CELLS = 3;
export const HOLE_PUSH_LEVELS = 6;
export const HOLE_MIN_FILL_WEIGHT = 1e-5;
export const HOLE_CELL_PX = 4;
export const HOLE_SCENE_SAMPLES = 4;

/** Quarter-resolution cell grid of a width x height frame. */
export function holeCellGrid(width, height) {
  return [Math.ceil(width / HOLE_CELL_PX), Math.ceil(height / HOLE_CELL_PX)];
}

/** Steps (in cells) that reach R along an axis and along a diagonal. */
export function holeReachSteps(cellsX, cellsY) {
  const radius = HOLE_RADIUS_FRACTION * Math.min(cellsX, cellsY);
  return { axis: Math.floor(radius), diagonal: Math.floor(radius * Math.SQRT1_2) };
}

/** Sizes of push levels 1..6 above a cellsX x cellsY grid (each ceil(size / 2)). */
export function holePushSizes(cellsX, cellsY) {
  const sizes = [];
  let w = Math.max(1, cellsX);
  let h = Math.max(1, cellsY);
  for (let k = 0; k < HOLE_PUSH_LEVELS; k += 1) {
    w = Math.ceil(w / 2);
    h = Math.ceil(h / 2);
    sizes.push([w, h]);
  }
  return sizes;
}

const MAX_MISSES = 8 - HOLE_MIN_DIRECTIONS;
const f = (x) => (Number.isInteger(x) ? `${x}.0` : String(x));

const VERTEX = /* glsl */ `
void main() {
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const COMMON = /* glsl */ `
bool insideGrid(ivec2 q, ivec2 size) {
  return q.x >= 0 && q.y >= 0 && q.x < size.x && q.y < size.y;
}
`;

// 1. Quarter-res premultiplied colour (rgb) and coverage (a).
const DOWN4_FRAGMENT = /* glsl */ `
uniform sampler2D tScene;
void main() {
  ivec2 size = textureSize(tScene, 0);
  ivec2 base = ivec2(gl_FragCoord.xy) * ${HOLE_CELL_PX};
  vec4 sum = vec4(0.0);
  float n = 0.0;
  for (int dy = 0; dy < ${HOLE_CELL_PX}; dy++) {
    for (int dx = 0; dx < ${HOLE_CELL_PX}; dx++) {
      ivec2 q = base + ivec2(dx, dy);
      if (q.x < size.x && q.y < size.y) {
        sum += texelFetch(tScene, q, 0);
        n += 1.0;
      }
    }
  }
  gl_FragColor = sum / max(n, 1.0);
}
`;

// 2. The rule for cells holding background. r = rule, g = open (coverage < 0.5).
const ENCLOSE_FRAGMENT = /* glsl */ `
${COMMON}
uniform sampler2D tQuarter;
uniform int uAxisSteps;
uniform int uDiagonalSteps;
const ivec2 DIRS[8] = ivec2[8](
  ivec2(1, 0), ivec2(1, 1), ivec2(0, 1), ivec2(-1, 1),
  ivec2(-1, 0), ivec2(-1, -1), ivec2(0, -1), ivec2(1, -1)
);
bool surfaceAt(ivec2 q) {
  return texelFetch(tQuarter, q, 0).a >= ${f(HOLE_SURFACE_COVERAGE)};
}
void main() {
  ivec2 size = textureSize(tQuarter, 0);
  ivec2 c = ivec2(gl_FragCoord.xy);
  float coverage = texelFetch(tQuarter, c, 0).a;
  float open = coverage < ${f(HOLE_SURFACE_COVERAGE)} ? 1.0 : 0.0;
  gl_FragColor = vec4(0.0, open, 0.0, 1.0);
  if (coverage >= 1.0) return;
  // First surface along each direction within 2 R (0 = none); whether the ray left the screen first.
  int first[8];
  bool left[8];
  for (int d = 0; d < 8; d++) {
    ivec2 dir = DIRS[d];
    int reach = 2 * ((d & 1) != 0 ? uDiagonalSteps : uAxisSteps);
    first[d] = 0;
    left[d] = false;
    for (int k = 1; k <= reach; k++) {
      ivec2 q = c + dir * k;
      if (!insideGrid(q, size)) {
        left[d] = true;
        break;
      }
      if (surfaceAt(q)) {
        first[d] = k;
        break;
      }
    }
  }
  int missed = 0;
  int missedDirection[${MAX_MISSES}];
  for (int d = 0; d < 8; d++) {
    int steps = (d & 1) != 0 ? uDiagonalSteps : uAxisSteps;
    int opposite = first[(d + 4) & 7];
    bool hit = first[d] > 0 && (first[d] <= steps || (opposite > 0 && first[d] + opposite <= 2 * steps));
    if (!hit) {
      if (missed == ${MAX_MISSES}) return;
      missedDirection[missed] = d;
      missed += 1;
    }
  }
  // Escape: a missed direction that runs on through background to the screen edge.
  int longest = max(size.x, size.y);
  for (int m = 0; m < missed; m++) {
    int d = missedDirection[m];
    if (first[d] > 0) continue;
    if (left[d]) return;
    ivec2 dir = DIRS[d];
    int start = 2 * ((d & 1) != 0 ? uDiagonalSteps : uAxisSteps) + 1;
    bool blocked = false;
    for (int k = start; k <= longest + 1; k++) {
      ivec2 q = c + dir * k;
      if (!insideGrid(q, size)) break;
      if (surfaceAt(q)) {
        blocked = true;
        break;
      }
    }
    if (!blocked) return;
  }
  gl_FragColor = vec4(1.0, open, 0.0, 1.0);
}
`;

// 3. r = feather weight w (0 when not enclosed), g = an enclosed cell in the 3 x 3 block.
const FEATHER_FRAGMENT = /* glsl */ `
${COMMON}
uniform sampler2D tRule;
// The rule holds and, for a mixed cell (surface majority with some background), no 8-neighbour is
// open background the rule rejects (the wall cells of a bay or a gap stay plain).
bool enclosedCell(ivec2 q, ivec2 size) {
  vec2 e = texelFetch(tRule, q, 0).rg;
  if (e.x < 0.5) return false;
  if (e.y >= 0.5) return true;
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      ivec2 n = q + ivec2(dx, dy);
      if ((dx != 0 || dy != 0) && insideGrid(n, size)) {
        vec2 g = texelFetch(tRule, n, 0).rg;
        if (g.y >= 0.5 && g.x < 0.5) return false;
      }
    }
  }
  return true;
}
void main() {
  ivec2 size = textureSize(tRule, 0);
  ivec2 c = ivec2(gl_FragCoord.xy);
  float nearby = 0.0;
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      ivec2 q = c + ivec2(dx, dy);
      if (nearby == 0.0 && insideGrid(q, size) && enclosedCell(q, size)) nearby = 1.0;
    }
  }
  if (!enclosedCell(c, size)) {
    gl_FragColor = vec4(0.0, nearby, 0.0, 1.0);
    return;
  }
  int nearest = ${HOLE_FEATHER_CELLS + 1};
  for (int dy = -${HOLE_FEATHER_CELLS}; dy <= ${HOLE_FEATHER_CELLS}; dy++) {
    for (int dx = -${HOLE_FEATHER_CELLS}; dx <= ${HOLE_FEATHER_CELLS}; dx++) {
      ivec2 q = c + ivec2(dx, dy);
      if (!insideGrid(q, size)) continue;
      vec2 e = texelFetch(tRule, q, 0).rg;
      if (e.y >= 0.5 && e.x < 0.5) nearest = min(nearest, max(abs(dx), abs(dy)));
    }
  }
  float w = nearest > ${HOLE_FEATHER_CELLS} ? 1.0 : float(nearest) / ${f(HOLE_FEATHER_CELLS + 1)};
  gl_FragColor = vec4(w, nearby, 0.0, 1.0);
}
`;

// 4. Push: mean of the in-grid 2 x 2 children (premultiplied colour, weight).
const PUSH_FRAGMENT = /* glsl */ `
${COMMON}
uniform sampler2D tFine;
void main() {
  ivec2 size = textureSize(tFine, 0);
  ivec2 c = ivec2(gl_FragCoord.xy) * 2;
  vec4 sum = vec4(0.0);
  float n = 0.0;
  for (int dy = 0; dy < 2; dy++) {
    for (int dx = 0; dx < 2; dx++) {
      ivec2 q = c + ivec2(dx, dy);
      if (insideGrid(q, size)) {
        sum += texelFetch(tFine, q, 0);
        n += 1.0;
      }
    }
  }
  gl_FragColor = n > 0.0 ? sum / n : vec4(0.0);
}
`;

// 5. Pull: what a level lacks comes from the (already pulled) coarser level.
const PULL_FRAGMENT = /* glsl */ `
uniform sampler2D tFine;
uniform sampler2D tCoarse;
void main() {
  ivec2 size = textureSize(tFine, 0);
  ivec2 c = ivec2(gl_FragCoord.xy);
  vec4 fine = texelFetch(tFine, c, 0);
  vec4 coarse = texture2D(tCoarse, (vec2(c) + 0.5) / vec2(size));
  gl_FragColor = fine + (1.0 - fine.a) * coarse;
}
`;

// 6. Composite to the screen (linear in, the renderer's output colour space out).
const COMPOSITE_FRAGMENT = /* glsl */ `
${COMMON}
uniform sampler2D tScene;
uniform sampler2D tFeather;
uniform sampler2D tFill;
uniform vec3 uBackground;
ivec2 cellOf(ivec2 p, ivec2 cells) {
  return min(p / ${HOLE_CELL_PX}, cells - 1);
}
void main() {
  ivec2 size = textureSize(tScene, 0);
  ivec2 cells = textureSize(tFeather, 0);
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 src = texelFetch(tScene, p, 0);
  vec2 cell = texelFetch(tFeather, cellOf(p, cells), 0).rg;
  vec3 color = src.rgb + (1.0 - src.a) * uBackground;
  if (cell.x > 0.0 || cell.y >= 0.5) {
    vec2 uv = ((vec2(p) + 0.5) * ${f(1 / HOLE_CELL_PX)}) / vec2(textureSize(tFill, 0));
    vec4 pulled = texture2D(tFill, uv);
    if (pulled.a > ${HOLE_MIN_FILL_WEIGHT.toExponential()}) {
      vec3 blur = pulled.rgb / pulled.a;
      vec3 hole = uBackground + cell.x * (blur - uBackground);
      color = src.rgb + (1.0 - src.a) * hole;
      if (src.a >= ${f(HOLE_PIXEL_COVERAGE)} && cell.y >= 0.5) {
        // 2 px soft blend at the hole rim (Chebyshev rings), weighted by the hole pixels' w.
        float ring1 = 0.0;
        float ring2 = 0.0;
        for (int dy = -2; dy <= 2; dy++) {
          for (int dx = -2; dx <= 2; dx++) {
            if (dx == 0 && dy == 0) continue;
            ivec2 q = p + ivec2(dx, dy);
            if (!insideGrid(q, size) || texelFetch(tScene, q, 0).a >= ${f(HOLE_PIXEL_COVERAGE)}) continue;
            float w = texelFetch(tFeather, cellOf(q, cells), 0).r;
            if (max(abs(dx), abs(dy)) == 1) ring1 = max(ring1, w);
            else ring2 = max(ring2, w);
          }
        }
        float k = ring1 > 0.0 ? ring1 * (2.0 / 3.0) : ring2 * (1.0 / 3.0);
        color = mix(color, blur, k);
      }
    }
  }
  gl_FragColor = vec4(color, 1.0);
  #include <colorspace_fragment>
}
`;

export const HOLE_BLUR_SHADERS = {
  down4: DOWN4_FRAGMENT,
  enclose: ENCLOSE_FRAGMENT,
  feather: FEATHER_FRAGMENT,
  push: PUSH_FRAGMENT,
  pull: PULL_FRAGMENT,
  composite: COMPOSITE_FRAGMENT,
};

/**
 * three compiles every WebGL2 shader as GLSL ES 3.00 (texelFetch, textureSize,
 * int loops); without an explicit glslVersion it also maps gl_FragColor and
 * texture2D, which these shaders use.
 */
function passMaterial(fragmentShader, uniforms) {
  return new THREE.ShaderMaterial({
    vertexShader: VERTEX,
    fragmentShader,
    uniforms,
    depthTest: false,
    depthWrite: false,
  });
}

function dataTarget(width, height, type, filter) {
  return new THREE.WebGLRenderTarget(width, height, {
    type,
    format: THREE.RGBAFormat,
    colorSpace: THREE.NoColorSpace,
    minFilter: filter,
    magFilter: filter,
    generateMipmaps: false,
    depthBuffer: false,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
  });
}

/**
 * Renders `scene` through the hole blur onto the current render target (the
 * canvas). `background` is the viewer background colour (any THREE.Color
 * input). Call setSize with the renderer's drawing-buffer size whenever it
 * changes.
 */
export class HoleBlurPass {
  constructor(renderer, { background = 0x111318 } = {}) {
    this.renderer = renderer;
    this.background = new THREE.Color(background);
    this.width = 0;
    this.height = 0;
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const triangle = new THREE.BufferGeometry();
    triangle.setAttribute("position", new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
    this.quad = new THREE.Mesh(triangle);
    this.quad.frustumCulled = false;
    this.quadScene = new THREE.Scene();
    this.quadScene.add(this.quad);
    this.floatType = renderer.extensions.has("EXT_color_buffer_float") ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this.materials = {
      down4: passMaterial(DOWN4_FRAGMENT, { tScene: { value: null } }),
      enclose: passMaterial(ENCLOSE_FRAGMENT, {
        tQuarter: { value: null },
        uAxisSteps: { value: 0 },
        uDiagonalSteps: { value: 0 },
      }),
      feather: passMaterial(FEATHER_FRAGMENT, { tRule: { value: null } }),
      push: passMaterial(PUSH_FRAGMENT, { tFine: { value: null } }),
      pull: passMaterial(PULL_FRAGMENT, { tFine: { value: null }, tCoarse: { value: null } }),
      composite: passMaterial(COMPOSITE_FRAGMENT, {
        tScene: { value: null },
        tFeather: { value: null },
        tFill: { value: null },
        uBackground: { value: this.background },
      }),
    };
    this.targets = null;
  }

  setSize(width, height) {
    width = Math.max(1, Math.floor(width));
    height = Math.max(1, Math.floor(height));
    if (this.targets && width === this.width && height === this.height) return;
    this.disposeTargets();
    this.width = width;
    this.height = height;
    const [cw, ch] = holeCellGrid(width, height);
    const half = this.floatType;
    this.targets = {
      scene: new THREE.WebGLRenderTarget(width, height, {
        type: THREE.UnsignedByteType,
        format: THREE.RGBAFormat,
        colorSpace: THREE.SRGBColorSpace,
        samples: HOLE_SCENE_SAMPLES,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        generateMipmaps: false,
        depthBuffer: true,
      }),
      quarter: dataTarget(cw, ch, half, THREE.NearestFilter),
      rule: dataTarget(cw, ch, THREE.UnsignedByteType, THREE.NearestFilter),
      feather: dataTarget(cw, ch, half, THREE.NearestFilter),
      // push[k] = level k + 1; pull[k] = pulled level k (pull[0] is the fill at cell resolution).
      push: holePushSizes(cw, ch).map(([w, h]) => dataTarget(w, h, half, THREE.LinearFilter)),
      pull: [[cw, ch], ...holePushSizes(cw, ch).slice(0, HOLE_PUSH_LEVELS - 1)].map(([w, h]) =>
        dataTarget(w, h, half, THREE.LinearFilter),
      ),
    };
    const reach = holeReachSteps(cw, ch);
    this.materials.enclose.uniforms.uAxisSteps.value = reach.axis;
    this.materials.enclose.uniforms.uDiagonalSteps.value = reach.diagonal;
  }

  /** The scene render of the last frame (premultiplied colour, coverage in alpha). */
  get sceneTarget() {
    return this.targets ? this.targets.scene : null;
  }

  drawQuad(material, target) {
    this.quad.material = material;
    this.renderer.setRenderTarget(target);
    this.renderer.render(this.quadScene, this.camera);
  }

  render(scene, camera) {
    const renderer = this.renderer;
    if (!this.targets) {
      const buffer = renderer.getDrawingBufferSize(new THREE.Vector2());
      this.setSize(buffer.x, buffer.y);
    }
    const t = this.targets;
    const m = this.materials;
    const outputTarget = renderer.getRenderTarget();
    const previousClear = renderer.getClearColor(new THREE.Color());
    const previousAlpha = renderer.getClearAlpha();
    const previousBackground = scene.background;
    const previousAutoClear = renderer.autoClear;

    scene.background = null;
    renderer.setClearColor(0x000000, 0);
    renderer.setRenderTarget(t.scene);
    renderer.render(scene, camera);
    scene.background = previousBackground;
    renderer.setClearColor(previousClear, previousAlpha);

    renderer.autoClear = false;
    m.down4.uniforms.tScene.value = t.scene.texture;
    this.drawQuad(m.down4, t.quarter);
    m.enclose.uniforms.tQuarter.value = t.quarter.texture;
    this.drawQuad(m.enclose, t.rule);
    m.feather.uniforms.tRule.value = t.rule.texture;
    this.drawQuad(m.feather, t.feather);

    let fine = t.quarter;
    t.push.forEach((target) => {
      m.push.uniforms.tFine.value = fine.texture;
      this.drawQuad(m.push, target);
      fine = target;
    });
    // F6 = P6; F(k) = P(k) + (1 - P(k).a) * bilinear(F(k + 1)) for k = 5 .. 0.
    let coarse = t.push[HOLE_PUSH_LEVELS - 1];
    for (let k = HOLE_PUSH_LEVELS - 1; k >= 0; k -= 1) {
      m.pull.uniforms.tFine.value = k === 0 ? t.quarter.texture : t.push[k - 1].texture;
      m.pull.uniforms.tCoarse.value = coarse.texture;
      this.drawQuad(m.pull, t.pull[k]);
      coarse = t.pull[k];
    }

    const composite = m.composite.uniforms;
    composite.tScene.value = t.scene.texture;
    composite.tFeather.value = t.feather.texture;
    composite.tFill.value = t.pull[0].texture;
    this.drawQuad(m.composite, outputTarget);
    renderer.autoClear = previousAutoClear;
  }

  /** True when a pass program failed to compile or link (the caller then draws directly). */
  failed() {
    return Object.values(this.materials).some(
      (material) => this.renderer.properties.get(material).currentProgram?.diagnostics?.runnable === false,
    );
  }

  disposeTargets() {
    if (this.targets) {
      const { scene, quarter, rule, feather, push, pull } = this.targets;
      [scene, quarter, rule, feather, ...push, ...pull].forEach((target) => target.dispose());
    }
    this.targets = null;
    this.width = 0;
    this.height = 0;
  }

  dispose() {
    this.disposeTargets();
    this.quad.geometry.dispose();
    Object.values(this.materials).forEach((material) => material.dispose());
  }
}
